/**
 * Structure Card
 *
 * Slide-by-slide classification showing narrative flow, design quality,
 * visual element badges, and missing section warnings.
 */

import React, { useState } from 'react';
import { View, Text, Pressable } from 'react-native';
import {
  LayoutGrid,
  ChevronDown,
  AlertTriangle,
  Image,
  Table2,
  PieChart,
  GitBranch,
  Camera,
  Map,
  Timer,
  Network,
} from 'lucide-react-native';
import Animated, { FadeIn, FadeInDown } from 'react-native-reanimated';
import type { RichExtraction } from '@/lib/supabase/edge-functions';
import { useThemeColors } from '@/lib/useThemeColors';

const VISUAL_ICONS: Record<string, typeof Image> = {
  chart: PieChart,
  table: Table2,
  diagram: GitBranch,
  photo: Camera,
  screenshot: Camera,
  map: Map,
  timeline: Timer,
  'org-chart': Network,
};

const SLIDE_TYPE_COLORS: Record<string, string> = {
  title: '#6366F1',
  problem: '#EF4444',
  solution: '#10B981',
  product: '#3B82F6',
  market: '#8B5CF6',
  traction: '#F59E0B',
  'business-model': '#06B6D4',
  competition: '#F97316',
  team: '#EC4899',
  financials: '#14B8A6',
  ask: '#22C55E',
  appendix: '#94A3B8',
  other: '#94A3B8',
};

interface StructureCardProps {
  structure: RichExtraction['structure'];
}

export function StructureCard({ structure }: StructureCardProps) {
  const colors = useThemeColors();
  const [expanded, setExpanded] = useState(false);

  const visibleSlides = expanded ? structure.slides : structure.slides.slice(0, 5);
  const hasMore = structure.slides.length > 5;

  const designQualityColor = structure.designQuality === 'polished' || structure.designQuality === 'design-heavy'
    ? colors.success
    : structure.designQuality === 'moderate'
    ? colors.warning
    : colors.text.tertiary;

  return (
    <Animated.View entering={FadeIn.delay(100).duration(400)}>
      <View
        className="rounded-2xl overflow-hidden"
        style={{
          backgroundColor: colors.surface.primary,
          borderWidth: 1,
          borderColor: colors.border.light,
        }}
      >
        {/* Header */}
        <View className="p-4 pb-3">
          <View className="flex-row items-center gap-2.5 mb-3">
            <View
              className="w-8 h-8 rounded-lg items-center justify-center"
              style={{ backgroundColor: colors.primary[50] }}
            >
              <LayoutGrid size={16} color={colors.primary[600]} />
            </View>
            <View className="flex-1">
              <Text style={{ color: colors.text.primary }} className="text-sm font-bold">
                Document Structure
              </Text>
              <Text style={{ color: colors.text.tertiary }} className="text-xs">
                {structure.totalPages} pages analyzed
              </Text>
            </View>
            <View
              className="px-2 py-1 rounded-md"
              style={{ backgroundColor: colors.surface.secondary }}
            >
              <Text style={{ color: colors.text.secondary }} className="text-xs font-medium">
                {structure.structureScore}/100
              </Text>
            </View>
          </View>

          {/* Narrative + Design quality pills */}
          <View className="flex-row gap-2 flex-wrap">
            <View className="flex-row items-center gap-1.5 px-2.5 py-1 rounded-full" style={{ backgroundColor: colors.surface.secondary }}>
              <Text style={{ color: colors.text.secondary }} className="text-xs">
                Narrative:
              </Text>
              <Text style={{ color: colors.text.primary }} className="text-xs font-semibold">
                {structure.narrativeFlow}
              </Text>
            </View>
            <View className="flex-row items-center gap-1.5 px-2.5 py-1 rounded-full" style={{ backgroundColor: colors.surface.secondary }}>
              <Text style={{ color: colors.text.secondary }} className="text-xs">
                Design:
              </Text>
              <Text style={{ color: designQualityColor }} className="text-xs font-semibold">
                {structure.designQuality}
              </Text>
            </View>
          </View>
        </View>

        {/* Slide list */}
        <View style={{ borderTopWidth: 1, borderTopColor: colors.border.light }}>
          {visibleSlides.map((slide, i) => {
            const typeColor = SLIDE_TYPE_COLORS[slide.type] || SLIDE_TYPE_COLORS.other;
            return (
              <Animated.View
                key={i}
                entering={FadeInDown.delay(i * 30).duration(250)}
                className="flex-row items-start px-4 py-2.5 gap-3"
                style={i < visibleSlides.length - 1 ? { borderBottomWidth: 0.5, borderBottomColor: colors.border.light } : undefined}
              >
                {/* Page number */}
                <View
                  className="w-7 h-7 rounded-md items-center justify-center mt-0.5"
                  style={{ backgroundColor: `${typeColor}15` }}
                >
                  <Text style={{ color: typeColor }} className="text-xs font-bold">
                    {slide.index}
                  </Text>
                </View>

                <View className="flex-1">
                  {/* Type badge + visual elements */}
                  <View className="flex-row items-center gap-2 mb-0.5">
                    <View className="px-2 py-0.5 rounded" style={{ backgroundColor: `${typeColor}18` }}>
                      <Text style={{ color: typeColor }} className="text-xs font-bold">
                        {slide.type}
                      </Text>
                    </View>
                    {slide.visualElements.length > 0 && (
                      <View className="flex-row gap-1">
                        {slide.visualElements.slice(0, 3).map((ve, vi) => {
                          const VeIcon = VISUAL_ICONS[ve] || Image;
                          return (
                            <View key={vi} className="w-5 h-5 rounded items-center justify-center" style={{ backgroundColor: colors.surface.secondary }}>
                              <VeIcon size={10} color={colors.text.tertiary} />
                            </View>
                          );
                        })}
                        {slide.visualElements.length > 3 && (
                          <Text style={{ color: colors.text.tertiary }} className="text-xs ml-0.5">
                            +{slide.visualElements.length - 3}
                          </Text>
                        )}
                      </View>
                    )}
                  </View>
                  <Text style={{ color: colors.text.secondary }} className="text-xs leading-4" numberOfLines={2}>
                    {slide.summary}
                  </Text>
                </View>
              </Animated.View>
            );
          })}

          {/* Show more */}
          {hasMore && (
            <Pressable
              onPress={() => setExpanded(!expanded)}
              className="flex-row items-center justify-center py-2.5 gap-1"
              style={{ borderTopWidth: 0.5, borderTopColor: colors.border.light }}
            >
              <Text style={{ color: colors.primary[600] }} className="text-xs font-semibold">
                {expanded ? 'Show less' : `Show all ${structure.slides.length} pages`}
              </Text>
              <ChevronDown
                size={12}
                color={colors.primary[600]}
                style={{ transform: [{ rotate: expanded ? '180deg' : '0deg' }] }}
              />
            </Pressable>
          )}
        </View>

        {/* Missing sections warning */}
        {structure.missingSections.length > 0 && (
          <View
            className="flex-row items-start gap-2.5 px-4 py-3 mx-3 mb-3 rounded-lg"
            style={{ backgroundColor: colors.amber.light }}
          >
            <AlertTriangle size={14} color={colors.warning} style={{ marginTop: 1 }} />
            <View className="flex-1">
              <Text style={{ color: colors.warning }} className="text-xs font-semibold mb-0.5">
                Missing sections
              </Text>
              <Text style={{ color: colors.text.secondary }} className="text-xs">
                {structure.missingSections.join(', ')}
              </Text>
            </View>
          </View>
        )}
      </View>
    </Animated.View>
  );
}
